export function variantsForColour(variants, index, colour) {
  return variants.filter(variant => index < 0 || variant.options[index] === colour);
}
export function chooseCardVariant(variants, index, colour) {
  const matches = variantsForColour(variants, index, colour);
  return matches.find(variant => variant.available) || matches[0] || null;
}
export function sizeLabel(variant, colourIndex) {
  const values = variant.options.filter((value, index) => index !== colourIndex && value.trim().toLowerCase() !== 'default title');
  return values.join(' / ') || 'One size';
}
export function variantURL(path, id, current) {
  const base = new URL(current);
  const url = new URL(path, base);
  url.searchParams.set('variant', String(id));
  if (base.searchParams.has('preview_theme_id')) url.searchParams.set('preview_theme_id', base.searchParams.get('preview_theme_id'));
  return url.href;
}

if (typeof document !== 'undefined' && !customElements.get('z-shopping-card')) {
  customElements.define('z-shopping-card', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try { this.data = JSON.parse(this.querySelector('[data-z-card-data]').textContent); }
      catch { return; }
      this.colour = this.dataset.colour;
      this.toggle = this.querySelector('[data-z-size-toggle]');
      this.panel = this.querySelector('[data-z-size-panel]');
      this.form = this.querySelector('product-form form');
      this.nativeSubmit = this.form?.querySelector('[type="submit"]');
      this.images = [...this.querySelectorAll('[data-z-card-image]')];
      this.abort = new AbortController();
      const events = {signal:this.abort.signal};
      this.addEventListener('click', event => {
        const swatch = event.target.closest('[data-z-swatch]');
        if (swatch) {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          if (this.isBusy()) return;
          this.colour = swatch.dataset.zSwatch;
          this.applyColour(this.colour, true);
          this.closeSizes();
          return;
        }
        if (event.target.closest('[data-z-size-toggle]')) {
          event.preventDefault();
          if (this.panel.hidden) this.openSizes(); else this.closeSizes();
          return;
        }
        const size = event.target.closest('[data-z-size-id]');
        if (size) {
          event.preventDefault();
          const variant = this.data.variants.find(item => String(item.id) === size.dataset.zSizeId);
          if (!variant?.available || this.data.release || this.isBusy() || !variantsForColour([variant],this.data.colourIndex,this.colour).length) return;
          this.form.querySelector('[name="id"]').value = variant.id;
          this.form.querySelector('[name="quantity"]').value = variant.minimum || 1;
          this.nativeSubmit.disabled = false;
          this.nativeSubmit.removeAttribute('aria-disabled');
          this.form.requestSubmit(this.nativeSubmit);
        }
      }, events);
      this.querySelectorAll('[data-z-swatch]').forEach(swatch => {
        swatch.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && !this.isBusy()) this.applyColour(swatch.dataset.zSwatch, false);
        }, events);
        swatch.addEventListener('pointerleave', () => this.applyColour(this.colour, false), events);
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel?.hidden) { this.closeSizes(); this.toggle?.focus(); }
      }, events);
      document.addEventListener('click', event => { if (!this.contains(event.target)) this.closeSizes(); }, events);
      this.applyColour(this.colour, true);
      if (this.data.release && this.dataset.releaseAt) {
        const deadline = Date.parse(this.dataset.releaseAt);
        if (deadline > Date.now() && (!window.zicoCardReleaseAt || deadline < window.zicoCardReleaseAt)) {
          clearTimeout(window.zicoCardReleaseTimer);
          window.zicoCardReleaseAt = deadline;
          window.zicoCardReleaseTimer = setTimeout(() => location.reload(),Math.min(deadline-Date.now()+500,2147483647));
        }
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.initialized = false; }
    isBusy() { return this.nativeSubmit?.getAttribute('aria-disabled') === 'true' || this.nativeSubmit?.classList.contains('loading'); }
    applyColour(colour, commit) {
      const media = this.data.colours.find(item => item.name === colour);
      const selected = chooseCardVariant(this.data.variants,this.data.colourIndex,colour);
      if (!media || !selected) return;
      const urls = [media.primary,media.secondary];
      this.images.forEach((image,index) => {
        image.hidden = !urls[index];
        if (urls[index]) {
          if (image.getAttribute('src') !== urls[index]) { image.removeAttribute('srcset'); image.src = urls[index]; }
          image.alt = `${this.data.title} - ${colour}${index ? ' - alternate view' : ''}`;
        }
      });
      this.toggleAttribute('data-has-secondary', Boolean(media.secondary));
      const label = this.querySelector('[data-z-colour-label]');
      if (label) label.textContent = colour;
      const price = this.querySelector('[data-z-card-price]');
      if (price) price.textContent = selected.priceLabel;
      const badge = this.querySelector('[data-z-card-badge]');
      if (badge) { badge.hidden = !this.data.release && selected.available; badge.textContent = this.data.release ? this.data.labels.release : this.data.labels.soldOut; }
      if (!commit) return;
      this.dataset.colour = colour;
      this.dataset.zProductColour = colour;
      this.querySelector('.z-shopping-card__image-link')?.setAttribute('aria-label',`${this.data.title}${colour ? ' - '+colour : ''}`);
      this.querySelectorAll('[data-z-product-link]').forEach(link => link.href = variantURL(this.data.url, selected.id, location.href));
      this.querySelectorAll('[data-z-swatch]').forEach(swatch => {
        const chosen = swatch.dataset.zSwatch === colour;
        swatch.setAttribute('aria-pressed',String(chosen));
        swatch.classList.toggle('is-selected',chosen);
      });
      if (this.toggle) this.toggle.hidden = !selected.available || this.data.release || this.data.requiresProductPage;
      this.renderSizes();
    }
    renderSizes() {
      const holder = this.querySelector('[data-z-size-options]');
      if (!holder) return;
      holder.replaceChildren();
      for (const variant of variantsForColour(this.data.variants,this.data.colourIndex,this.colour)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.zSizeId = String(variant.id);
        button.textContent = sizeLabel(variant,this.data.colourIndex);
        button.disabled = !variant.available;
        button.setAttribute('aria-label',`${this.colour} ${button.textContent}${variant.available ? '' : ' - '+this.data.labels.soldOut}`);
        holder.append(button);
      }
    }
    openSizes() {
      document.querySelectorAll('z-shopping-card').forEach(card => { if (card !== this) card.closeSizes?.(); });
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded','true');
      this.panel.querySelector('button:not([disabled])')?.focus({preventScroll:true});
    }
    closeSizes() { if (this.panel) this.panel.hidden = true; this.toggle?.setAttribute('aria-expanded','false'); }
  });
}
